import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { Table, cell } from '../components/ui';
import { businessDate, dateTime, duration, MYT, preciseTitle } from '../lib/time';
import { useState } from 'react';

type Coverage = {
  tenantId: string;
  coverage: Array<{
    doctype: string;
    documents: number;
    withDetail: number;
    oldest: string | null;
    newest: string | null;
  }>;
  outstandingTrustworthy: boolean;
  caveat: string | null;
};

type OutstandingLine = {
  docno: string;
  docdate: string;
  partnerName: string | null;
  itemcode: string | null;
  location: string | null;
  smartlinkDeptCode: string | null;
  planQty: string;
  postedQty: string;
  outstandingQty: string;
};

const api = async <T,>(path: string): Promise<T> => {
  // credentials: the session is an httpOnly cookie and fetch omits it by default.
  const res = await fetch(path, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`${path} → ${res.status}`);
  return res.json() as Promise<T>;
};

/**
 * Phase 1 read-only dashboard.
 *
 * The coverage caveat sits ABOVE the numbers by design. Outstanding is derived from the GR/DO
 * chain, so while line detail is still loading the figures overstate what is open — showing them
 * without that context is the most damaging thing this screen could do.
 */
type JobHealth = {
  scheduled: Array<{
    name: string;
    cron: string;
    lastRun: {
      status: string;
      startedAt: string;
      durationSeconds: number;
      error: string | null;
    } | null;
  }>;
  activeLocks: Array<{ jobName: string; lockedBy: string; lockedAt: string }>;
  stuckRuns: Array<{ jobName: string; runningForSeconds: number }>;
  cursors: Array<{
    resource: string;
    watermarkAt: string | null;
    watermarkStalled: boolean;
    docsSeen: number;
    lastError: string | null;
    /** Verdict from `domain/health/syncHealth` — see the System health screen. */
    status: 'ok' | 'stale' | 'down' | 'unknown';
    staleForMs: number | null;
  }>;
  sync: {
    overall: 'ok' | 'stale' | 'down' | 'unknown';
    systems: Array<{ system: string; status: string; worstResource: string | null }>;
  };
};

/** Plain words beside the colour, so the state survives a screenshot and a colour-blind reader. */
const CURSOR_LABEL: Record<string, string> = {
  ok: 'current',
  stale: 'behind',
  down: 'stalled',
  unknown: 'never run',
};

const CURSOR_TONE: Record<string, string> = {
  ok: 'text-emerald-700',
  stale: 'text-amber-700',
  down: 'text-red-700',
  unknown: 'text-slate-500',
};

export default function RfidDashboard() {
  const [direction, setDirection] = useState<'IN' | 'OUT'>('IN');

  const health = useQuery({
    queryKey: ['health'],
    queryFn: () => api<JobHealth>('/api/v1/health/jobs'),
    refetchInterval: 30_000,
  });

  const coverage = useQuery({
    queryKey: ['coverage'],
    queryFn: () => api<Coverage>('/api/v1/coverage'),
    refetchInterval: 30_000,
  });

  const outstanding = useQuery({
    queryKey: ['outstanding', direction],
    queryFn: () =>
      api<{ total: number; lines: OutstandingLine[] }>(
        `/api/v1/outstanding?direction=${direction}&limit=50`,
      ),
    refetchInterval: 30_000,
  });

  return (
    <div className="min-h-screen bg-brand-50 text-slate-900">
      <header className="border-b border-slate-200 bg-white px-6 py-4">
        <h1 className="text-lg font-semibold">rfid-mw</h1>
        <p className="text-sm text-slate-500">
          LSK × Smartlink RFID — tenant {coverage.data?.tenantId ?? '…'}
        </p>
      </header>

      <main className="mx-auto max-w-6xl space-y-4 p-6">
        {coverage.data && !coverage.data.outstandingTrustworthy && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
            <p className="text-sm font-medium text-amber-900">Figures below are provisional</p>
            <p className="mt-1 text-sm text-amber-800">{coverage.data.caveat}</p>
          </div>
        )}

        <section className="rounded-lg border border-slate-200 bg-white p-5">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
            Read-model coverage
          </h2>
          {coverage.isLoading && <p className="text-sm text-slate-500">Loading…</p>}
          {coverage.error && (
            <p className="text-sm text-red-600">
              API unreachable: {(coverage.error as Error).message}
            </p>
          )}
          {coverage.data && (
            <Table
              head={['Doc', 'Documents', 'With line detail', 'Range']}
              rows={coverage.data.coverage.map((c) => {
                const pct = c.documents ? Math.round((c.withDetail / c.documents) * 100) : 0;
                return [
                  <span className="font-mono">{c.doctype}</span>,
                  c.documents.toLocaleString(),
                  // Sort by completeness, not by the formatted "12,345 (98%)" string.
                  cell(
                    pct,
                    <span className={pct === 100 ? 'text-emerald-700' : 'text-amber-700'}>
                      {c.withDetail.toLocaleString()} ({pct}%)
                    </span>,
                  ),
                  cell(
                    c.oldest ?? null,
                    <span className="text-slate-500">
                      {businessDate(c.oldest)} → {businessDate(c.newest)}
                    </span>,
                  ),
                ];
              })}
            />
          )}
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Open lines {outstanding.data ? `(${outstanding.data.total})` : ''}
            </h2>
            <div className="flex gap-1 rounded-md bg-slate-100 p-1">
              {(['IN', 'OUT'] as const).map((d) => (
                <button
                  key={d}
                  onClick={() => setDirection(d)}
                  className={`rounded px-3 py-1 text-sm ${
                    direction === d ? 'bg-white font-medium shadow-sm' : 'text-slate-600'
                  }`}
                >
                  {d === 'IN' ? 'Inbound (PO → GR)' : 'Outbound (SO → DO)'}
                </button>
              ))}
            </div>
          </div>

          {outstanding.isLoading && <p className="text-sm text-slate-500">Loading…</p>}
          {outstanding.data && outstanding.data.lines.length === 0 && (
            <p className="text-sm text-slate-500">No open lines.</p>
          )}
          {outstanding.data && outstanding.data.lines.length > 0 && (
            <Table
              head={[
                'Doc',
                'Doc date',
                'Partner',
                'Item',
                'Location',
                'Plan',
                'Posted',
                'Outstanding',
              ]}
              rows={outstanding.data.lines.map((l) => [
                <span className="font-mono text-xs">{l.docno}</span>,
                // A SQL Accounting business date: the day the document is dated, with no time and
                // no zone. Rendered as stored — converting it would move the document a day.
                <span className="text-slate-500">{businessDate(l.docdate)}</span>,
                l.partnerName,
                <span className="font-mono text-xs">{l.itemcode}</span>,
                <>
                  <span className="font-mono text-xs">{l.location}</span>
                  {!l.smartlinkDeptCode && (
                    <span
                      className="ml-1 text-amber-600"
                      title="No Smartlink department — cannot enter the RFID flow"
                    >
                      ⚠
                    </span>
                  )}
                </>,
                // Quantities are DECIMAL strings from the API and are never parsed to a float for
                // display. Number() here feeds the comparator only — it never reaches the screen.
                cell(Number(l.planQty), <span className="tabular-nums">{l.planQty}</span>),
                cell(
                  Number(l.postedQty),
                  <span className="tabular-nums text-slate-500">{l.postedQty}</span>,
                ),
                cell(
                  Number(l.outstandingQty),
                  <span className="font-medium tabular-nums">{l.outstandingQty}</span>,
                ),
              ])}
            />
          )}
        </section>

        {/* Screen 10 — sync & job health. */}
        <section className="rounded-lg border border-slate-200 bg-white p-5">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
            Sync &amp; job health
          </h2>

          {/* The panel used to render an outage as an ordinary table. It is the first thing an
              operator looks at each morning, so a dead sync says so here and points at the screen
              that explains it. */}
          {health.data &&
            health.data.sync.overall !== 'ok' &&
            health.data.sync.overall !== 'unknown' && (
              <div
                data-testid="sync-down-banner"
                className="mb-3 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800"
              >
                <span className="font-medium">
                  {health.data.sync.overall === 'down'
                    ? 'A sync has stopped.'
                    : 'A sync is falling behind.'}
                </span>{' '}
                {health.data.sync.systems
                  .filter((s) => s.status !== 'ok')
                  .map((s) => (s.system === 'SQLACC' ? 'SQL Accounting' : 'Smartlink'))
                  .join(', ')}{' '}
                — figures on this page are as of the last successful run.{' '}
                <Link to="/admin/health" className="font-medium underline">
                  System health
                </Link>
              </div>
            )}

          {health.data?.stuckRuns.length ? (
            <div className="mb-3 rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800">
              {health.data.stuckRuns.length} job(s) running for over 12 hours — likely stuck:{' '}
              {health.data.stuckRuns.map((s) => s.jobName).join(', ')}
            </div>
          ) : null}

          {health.data && (
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <h3 className="mb-2 text-xs font-medium uppercase text-slate-400">
                  Scheduled ({MYT})
                </h3>
                <table className="w-full text-sm">
                  <tbody>
                    {health.data.scheduled.map((j) => (
                      <tr key={j.name} className="border-b border-slate-100">
                        <td className="py-1 font-mono text-xs">{j.name}</td>
                        <td className="py-1 font-mono text-xs text-slate-500">{j.cron}</td>
                        <td className="py-1 text-right">
                          {j.lastRun ? (
                            <span
                              className={
                                j.lastRun.status === 'ok' ? 'text-emerald-700' : 'text-red-700'
                              }
                              title={`Last run ${preciseTitle(j.lastRun.startedAt)}`}
                            >
                              {j.lastRun.status} · {j.lastRun.durationSeconds}s ·{' '}
                              {dateTime(j.lastRun.startedAt)}
                            </span>
                          ) : (
                            <span className="text-slate-400">never run</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {health.data.activeLocks.length > 0 && (
                  <p className="mt-2 text-xs text-slate-500">
                    Holding a lock: {health.data.activeLocks.map((l) => l.jobName).join(', ')}
                  </p>
                )}
              </div>

              <div>
                <h3 className="mb-2 text-xs font-medium uppercase text-slate-400">
                  Sync cursors ({MYT})
                </h3>
                <table className="w-full text-sm">
                  <tbody>
                    {health.data.cursors.map((c) => (
                      <tr
                        key={c.resource}
                        data-testid={`cursor-${c.resource}`}
                        className="border-b border-slate-100"
                      >
                        <td className="py-1 font-mono text-xs">{c.resource}</td>
                        <td className="py-1 text-right text-slate-500">
                          {c.docsSeen.toLocaleString()} docs
                        </td>
                        <td className="py-1 text-right">
                          {/* Was a bare timestamp in the same grey whether it was a minute old or
                              three days. The verdict and the elapsed time are the whole point. */}
                          <span
                            className={CURSOR_TONE[c.status] ?? 'text-slate-500'}
                            title={preciseTitle(c.watermarkAt)}
                          >
                            {CURSOR_LABEL[c.status] ?? c.status}
                            {c.status !== 'ok' && c.staleForMs !== null
                              ? ` ${duration(c.staleForMs)}`
                              : ''}
                          </span>
                          {/* A never-advanced watermark means every run re-reads everything.
                              Expected mid-backfill (it is held back so deferred documents are
                              not skipped); a problem once the backfill is done. */}
                          {c.watermarkStalled && (
                            <span className="ml-2 text-amber-700">no watermark</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        <p className="text-xs text-slate-500">
          Screens 1–12 are specified in <code>docs/dashboard-spec.md</code>. Verified facts about
          tenant 04007 live in <code>docs/discovery.md</code>.
        </p>
      </main>
    </div>
  );
}
